import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Chip, IconButton, Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { Palette, spacing, useThemePalette } from '../../../theme';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
}

const SkillsForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [draft, setDraft] = useState('');

  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) {
      setDraft('');
      return;
    }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const remove = (s: string) => onChange(value.filter((x) => x !== s));

  return (
    <View style={styles.container}>
      <View style={styles.inputRow}>
        <TextInput
          mode="outlined"
          label={t('resume.fields.skillPlaceholder')}
          value={draft}
          onChangeText={setDraft}
          onSubmitEditing={add}
          returnKeyType="done"
          style={styles.input}
          dense
        />
        <IconButton
          icon="plus"
          mode="contained"
          onPress={add}
          containerColor={palette.primary}
          iconColor="#0F1115"
          disabled={!draft.trim()}
        />
      </View>
      {value.length > 0 ? (
        <View style={styles.chipRow}>
          {value.map((s) => (
            <Chip
              key={s}
              onClose={() => remove(s)}
              style={styles.chip}
              textStyle={styles.chipText}
            >
              {s}
            </Chip>
          ))}
        </View>
      ) : (
        <Text style={styles.empty}>{t('resume.empty.skillsHint')}</Text>
      )}
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.md },
    inputRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    input: { flex: 1 },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    chip: { backgroundColor: p.surfaceMuted },
    chipText: { color: p.textPrimary },
    empty: { color: p.textMuted, fontSize: 13, fontStyle: 'italic' },
  });

export default SkillsForm;
